import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, Plane, PlaneLanding, Repeat, ShieldCheck } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { API, formatGBP } from "@/lib/site";

const inputCls =
  "w-full rounded-xl border border-white/15 bg-[#0b101b] px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-[#38bdf8] focus:outline-none";
const labelCls = "mb-2 block text-[11px] font-mono-lbl uppercase tracking-wider text-slate-400";

export const BookingWidget = ({ config }) => {
  const [form, setForm] = useState({
    direction: "to_airport",
    pickup_zone: "",
    airport: "LBA",
    date: "",
    time: "",
    passengers: "2",
    vehicle: "saloon",
    flight_number: "",
    return_journey: false,
    return_date: "",
    return_time: "",
    return_flight_number: "",
  });
  const [details, setDetails] = useState({ name: "", email: "", phone: "" });
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setQuote(null);
  };

  const getQuote = async () => {
    if (!config) return toast.error("Booking options still loading — one moment");
    if (!form.pickup_zone) return toast.error("Choose your pickup area");
    if (!form.date) return toast.error("Choose your travel date");
    if (!form.time) return toast.error("Choose a pickup time");
    if (form.return_journey && (!form.return_date || !form.return_time))
      return toast.error("Choose your return date and time");
    if (form.return_journey && form.return_date < form.date)
      return toast.error("Return date can't be before the outward journey");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/quote`, {
        pickup_zone: form.pickup_zone,
        airport: form.airport,
        vehicle: form.vehicle,
        passengers: parseInt(form.passengers, 10),
        return_journey: form.return_journey,
        return_date: form.return_date,
        return_time: form.return_time,
      });
      setQuote(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not get a quote");
    } finally {
      setLoading(false);
    }
  };

  const pay = async () => {
    if (!details.name.trim() || !details.email.trim() || !details.phone.trim())
      return toast.error("Add your name, email and phone so we can confirm your booking");
    setPaying(true);
    try {
      const { data: booking } = await axios.post(`${API}/bookings`, {
        ...form,
        passengers: parseInt(form.passengers, 10),
        ...details,
      });
      const { data: checkout } = await axios.post(`${API}/payments/checkout`, {
        booking_id: booking.id,
        origin_url: window.location.origin,
      });
      window.location.href = checkout.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start secure checkout");
      setPaying(false);
    }
  };

  const zoneLabel = form.direction === "to_airport" ? "Pickup — your area" : "Drop-off — your area";

  return (
    <div
      id="book"
      data-testid="booking-widget"
      className="d1-fade-form relative isolate scroll-mt-28 rounded-3xl border border-[#38bdf8]/30 bg-gradient-to-b from-[#151e32] to-[#0d1424] p-6 shadow-2xl sm:p-8"
    >
      {/* Direction toggle */}
      <div className="mb-5 inline-flex rounded-full border border-white/15 bg-[#0b101b] p-1" data-testid="journey-direction-toggle">
        {[
          { v: "to_airport", label: "To Airport", Icon: Plane },
          { v: "from_airport", label: "From Airport", Icon: PlaneLanding },
        ].map(({ v, label, Icon }) => (
          <button
            key={v}
            type="button"
            data-testid={`journey-${v === "to_airport" ? "to-airport" : "from-airport"}-button`}
            onClick={() => set("direction", v)}
            className={`flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
              form.direction === v ? "bg-[#38bdf8] text-[#0b101b]" : "text-slate-400 hover:text-white"
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <label className={labelCls}>{zoneLabel}</label>
          <Select value={form.pickup_zone} onValueChange={(v) => set("pickup_zone", v)}>
            <SelectTrigger data-testid="pickup-zone-select" className="h-auto rounded-xl border-white/15 bg-[#0b101b] px-4 py-3 text-sm">
              <SelectValue placeholder="Choose area / town" />
            </SelectTrigger>
            <SelectContent>
              {(config?.zones || []).map((z) => (
                <SelectItem key={z} value={z} data-testid={`zone-option-${z.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{z}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className={labelCls}>Airport</label>
          <Select value={form.airport} onValueChange={(v) => set("airport", v)}>
            <SelectTrigger data-testid="airport-select" className="h-auto rounded-xl border-white/15 bg-[#0b101b] px-4 py-3 text-sm">
              <SelectValue placeholder="Choose airport" />
            </SelectTrigger>
            <SelectContent>
              {(config?.airports || []).map((a) => (
                <SelectItem key={a.code} value={a.code} data-testid={`airport-option-${a.code.toLowerCase()}`}>{a.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className={labelCls}>Travel Date</label>
          <input data-testid="travel-date-input" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Pickup Time</label>
          <input data-testid="pickup-time-input" type="time" value={form.time} onChange={(e) => set("time", e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Passengers</label>
          <Select value={form.passengers} onValueChange={(v) => set("passengers", v)}>
            <SelectTrigger data-testid="passengers-select" className="h-auto rounded-xl border-white/15 bg-[#0b101b] px-4 py-3 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                <SelectItem key={n} value={String(n)} data-testid={`passengers-option-${n}`}>{n} passenger{n > 1 ? "s" : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className={labelCls}>Vehicle</label>
          <Select value={form.vehicle} onValueChange={(v) => set("vehicle", v)}>
            <SelectTrigger data-testid="vehicle-select" className="h-auto rounded-xl border-white/15 bg-[#0b101b] px-4 py-3 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(config?.vehicles || []).map((v) => (
                <SelectItem key={v.id} value={v.id} data-testid={`vehicle-option-${v.id}`}>{v.name} · up to {v.seats}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className={labelCls}>Flight Number (Tracking)</label>
          <input
            data-testid="flight-number-input"
            type="text"
            value={form.flight_number}
            onChange={(e) => set("flight_number", e.target.value)}
            placeholder="e.g. LS251 (Jet2)"
            className={inputCls}
          />
        </div>
        <div className="flex flex-col justify-end">
          <button
            type="button"
            data-testid="get-quote-button"
            onClick={getQuote}
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#38bdf8] px-6 py-3.5 text-sm font-bold uppercase tracking-wider text-[#0b101b] shadow-[0_0_20px_rgba(56,189,248,0.4)] transition-colors hover:bg-white disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <span>Get Fixed Quote</span>}
            {!loading && <span>&rarr;</span>}
          </button>
        </div>
      </div>

      {/* Return journey toggle */}
      <div className="mt-5 flex flex-wrap items-center gap-4 rounded-2xl border border-white/10 bg-[#0b101b]/60 px-5 py-4">
        <div className="flex items-center gap-3">
          <Switch
            data-testid="return-journey-toggle"
            checked={form.return_journey}
            onCheckedChange={(v) => set("return_journey", v)}
          />
          <span className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-white">
            <Repeat className="h-4 w-4 text-[#38bdf8]" /> Add return journey
          </span>
          <span className="rounded-full bg-emerald-400/15 px-2.5 py-0.5 font-mono-lbl text-[10px] uppercase tracking-widest text-emerald-400">
            Save 10%
          </span>
        </div>
        {form.return_journey && (
          <div className="grid w-full grid-cols-1 gap-4 pt-1 sm:grid-cols-3" data-testid="return-leg-fields">
            <div>
              <label className={labelCls}>Return Date</label>
              <input data-testid="return-date-input" type="date" value={form.return_date} onChange={(e) => set("return_date", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Return Pickup Time</label>
              <input data-testid="return-time-input" type="time" value={form.return_time} onChange={(e) => set("return_time", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Return Flight No. (Tracking)</label>
              <input data-testid="return-flight-number-input" type="text" value={form.return_flight_number} onChange={(e) => set("return_flight_number", e.target.value)} placeholder="Inbound flight" className={inputCls} />
            </div>
          </div>
        )}
      </div>

      {quote && (
        <div className="mt-6 rounded-2xl border border-[#38bdf8]/40 bg-[#0b101b]/70 p-6" data-testid="quote-panel">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
            <div>
              <div className="font-mono-lbl text-[11px] uppercase tracking-widest text-[#38bdf8]">
                {quote.return_journey ? "Fixed round-trip fare — nothing more to pay" : "Fixed fare — nothing more to pay"}
              </div>
              <div className="mt-1 font-disp text-5xl text-white" data-testid="quote-amount">{formatGBP(quote.total_gbp ?? quote.amount_gbp)}</div>
              <div className="mt-2 text-xs text-slate-400" data-testid="quote-summary">
                {form.direction === "to_airport"
                  ? `${quote.pickup_zone} → ${quote.airport_name}`
                  : `${quote.airport_name} → ${quote.pickup_zone}`}
                {" · "}{form.date} {form.time}{" · "}{quote.vehicle_name}{" · "}{quote.passengers} passenger{quote.passengers > 1 ? "s" : ""}
              </div>
              {quote.return_journey && (
                <div className="mt-3 space-y-1 border-t border-white/10 pt-3 font-mono-lbl text-xs text-slate-300" data-testid="quote-breakdown">
                  <div className="flex justify-between gap-8">
                    <span>Outward · {form.date}</span>
                    <span>{formatGBP(quote.outward_gbp)}</span>
                  </div>
                  <div className="flex justify-between gap-8">
                    <span>Return · {quote.return_date} {quote.return_time}</span>
                    <span>{formatGBP(quote.return_gbp)}</span>
                  </div>
                  <div className="flex justify-between gap-8 text-emerald-400" data-testid="quote-discount-row">
                    <span>Round-trip saving (10%)</span>
                    <span>−{formatGBP(quote.discount_gbp)}</span>
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 font-mono-lbl text-xs text-emerald-400">
              <ShieldCheck className="h-4 w-4" /> Free cancellation up to 12h before pickup
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 border-t border-white/10 pt-6 sm:grid-cols-3">
            <div>
              <label className={labelCls}>Lead Passenger Name</label>
              <input data-testid="lead-name-input" type="text" value={details.name} onChange={(e) => setDetails((d) => ({ ...d, name: e.target.value }))} placeholder="Full name" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Email (voucher)</label>
              <input data-testid="lead-email-input" type="email" value={details.email} onChange={(e) => setDetails((d) => ({ ...d, email: e.target.value }))} placeholder="you@example.com" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Mobile Number</label>
              <input data-testid="lead-phone-input" type="tel" value={details.phone} onChange={(e) => setDetails((d) => ({ ...d, phone: e.target.value }))} placeholder="07…" className={inputCls} />
            </div>
          </div>

          <button
            type="button"
            data-testid="pay-with-stripe-button"
            onClick={pay}
            disabled={paying}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-[#38bdf8] px-6 py-4 text-sm font-bold uppercase tracking-wider text-[#0b101b] shadow-[0_0_24px_rgba(56,189,248,0.5)] transition-colors hover:bg-white disabled:opacity-60 sm:w-auto"
          >
            {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <span>Pay {formatGBP(quote.total_gbp ?? quote.amount_gbp)} with Stripe</span>}
            {!paying && <ArrowRight className="h-4 w-4" />}
          </button>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-4 font-mono-lbl text-xs text-slate-400">
        <span className="flex items-center gap-2 text-slate-300">
          <span className="h-2 w-2 rounded-full bg-emerald-400"></span> Free cancellation up to 12h before departure
        </span>
        <span className="text-[#38bdf8]/90">Stripe instant 3D-secure checkout</span>
      </div>
    </div>
  );
};
